import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccPayerType = 'managedCare' | 'medicaid' | 'medicareA' | 'medicareB' | 'medicareD' | 'other' | 'outpatient' | 'private';
export const PCC_PAYER_TYPES: ReadonlyArray<{ label: string; value: PccPayerType }> = [
	{ label: 'Managed Care', value: 'managedCare' },
	{ label: 'Medicaid', value: 'medicaid' },
	{ label: 'Medicare A', value: 'medicareA' },
	{ label: 'Medicare B', value: 'medicareB' },
	{ label: 'Medicare D', value: 'medicareD' },
	{ label: 'Other', value: 'other' },
	{ label: 'Outpatient', value: 'outpatient' },
	{ label: 'Private', value: 'private' },
];
export type PccEpisodeOfCareStatus = 'active' | 'resolved';

export class PccCareEpisodeParams {
	payerType?: PccPayerType;

	statuses?: PccEpisodeOfCareStatus[];
}
/**
 * Represents a Diagnosis-Related Group (DRG) coding entry
 */
export class DrgCoding {
	/**
	 * Represents the code of the Diagnosis-Related Group (DRG) in the episode
	 */
	code: number;

	/**
	 * Represents the description of the Diagnosis-Related Group (DRG) in the episode
	 */
	description: string;

	/**
	 * Represents the coding system of the Diagnosis-Related Group (DRG) in the episode. Possible values are 'CMS','ICD-10', or 'Custom'.
	 */
	system: 'CMS' | 'ICD-10' | 'Custom';
}

/**
 * Represents a list of DRG codes
 */
export class DrgList {
	/**
	 * List of array of codes for the Diagnosis-Related Group (DRG). Currently, only a single object is returned in the array.
	 */
	coding: DrgCoding[];
}

/**
 * Represents an episode of care for a patient in PointClickCare
 *
 * SimpliScreen maps the care episode to the following string for embedding in the RAG:
 * name: {name}, status: {status}, type: {type}, startDate: {startDate}, endDate: {endDate}, duration: {duration}, model: {modelDescription}, payerName: {payerName}, payerType: {payerType}, payerCode: {payerCode}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/episodes-of-care?patientId={patientId}
 */
export class PccEpisodeOfCare extends PccAuditInfo {
	/**
	 * Represents the estimated Diagnosis-Related Group (DRG). The estimated DRG represents the DRG tracked by the care setting (for e.g. hospital) where the patient came from to the facility.
	 */
	actualDrg: DrgList;

	/**
	 * Represents the duration (number of days) for the episode of care. If the value returned is '0', that indicates that the duration for the episode is unknown.
	 */
	duration: number;

	/**
	 * Represents the end date of the episode. The date format is YYYY-MM-DD.
	 * This field is only present when status is 'resolved'.
	 */
	endDate?: string;

	/**
	 * PointClickCare's unique identifier for the episode of care
	 */
	episodeOfCareId: number;

	/**
	 * Represents the estimated Diagnosis-Related Group (DRG). The estimated DRG represents the DRG tracked by the care setting (for e.g. hospital) where the patient came from to the facility.
	 */
	estimatedDrg: DrgList;

	/**
	 * Represents the model for the episode of care. Possible values are 0, 1, 2, 3, 4 or 5.
	 * If the value returned is '0', that indicates that the model is unknown.
	 *
	 * BPCI Model 1: Retrospective Acute Care Hospital Stay Only
	 * BPCI Model 2: Retrospective Acute & Post Acute Care Episode
	 * BPCI Model 3: Retrospective Post Acute Care Only
	 * BPCI Model 4: Prospective Acute Care Hospital Stay Only
	 * BPCI Model 5: Comprehensive Care for Joint Replacement
	 */
	// eslint-disable-next-line @typescript-eslint/no-magic-numbers
	model: 0 | 1 | 2 | 3 | 4 | 5;

	modelDescription: string;

	/**
	 * Represents the description for the episode
	 */
	name: string;

	/**
	 * Represents the payer's code of the payer associated in the episode
	 */
	payerCode: string;

	/**
	 * Represents the name of the payer associated in the episode
	 */
	payerName: string;

	/**
	 * Represents the type of the payer associated in the episode. Possible values are:
	 * managedCare, medicaid, medicareA, medicareB, medicareD, other, outpatient, private
	 */
	payerType: PccPayerType;

	/**
	 * Represents the start date of the episode. The date format is YYYY-MM-DD.
	 */
	startDate: string;

	/**
	 * Represents the status for episode of care. Possible values are "active" or "resolved".
	 */
	status: PccEpisodeOfCareStatus;

	/**
	 * Represents the library for the episode. Possible values are 'CMS' or 'Custom'.
	 */
	type: 'CMS' | 'Custom';

	/**
	 * Represents the details of the episode of care.
	 *
	 * SimpliScreen maps the episode of care to the following string for embedding in the RAG:
	 * name: {name}, status: {status}, type: {type}, startDate: {startDate}, endDate: {endDate}, duration: {duration}, model: {modelDescription}, payerName: {payerName}, payerType: {payerType}, payerCode: {payerCode}
	 *
	 */
	details: string;

	constructor(partial: Partial<PccEpisodeOfCare>) {
		super();
		Object.assign(this, partial);

		/* eslint-disable @typescript-eslint/no-magic-numbers */
		switch (this.model) {
			case 1:
				this.modelDescription = 'Retrospective Acute Care Hospital Stay Only';
				break;
			case 2:
				this.modelDescription = 'Retrospective Acute & Post Acute Care Episode';
				break;
			case 3:
				this.modelDescription = 'Retrospective Post Acute Care Only';
				break;
			case 4:
				this.modelDescription = 'Prospective Acute Care Hospital Stay Only';
				break;
			case 5:
				this.modelDescription = 'Comprehensive Care for Joint Replacement';
				break;
			default:
				this.modelDescription = 'Unknown';
				break;
		}
		/* eslint-enable @typescript-eslint/no-magic-numbers */
	}

	@Expose({ name: 'details' })
	getDetails(skipName = false) {
		const details = [`Data Type: Care Episode`];
		if (this.name && !skipName) details.push(`Name: ${this.name}`);
		if (this.status) details.push(`Status: ${this.status}`);
		if (this.type) details.push(`Type: ${this.type}`);
		if (this.startDate) details.push(`Start Date: ${this.startDate}`);
		if (this.endDate) details.push(`End Date: ${this.endDate}`);
		if (this.duration) details.push(`Duration: ${this.duration}`);
		if (this.modelDescription) details.push(`Model: ${this.modelDescription}`);
		if (this.payerName) details.push(`Payer Name: ${this.payerName}`);
		if (this.payerType) details.push(`Payer Type: ${this.payerType}`);
		if (this.payerCode) details.push(`Payer Code: ${this.payerCode}`);
		return details.join(', ');
	}
}
